import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from '@common/config';
import { useNumberParam, useSessionContext, useSyncListByUniqueSelection } from '@common/hooks';
import { useCurrentCampagne } from '@common/hooks/campagne';
import {
  EtablissementInfoDto,
  EtablissementProjection,
  Page,
  Sorter,
  useDownloadSelectedCampagneRncpsExcelMutation,
  useGetCampagneRncpsQuery,
  useGetOrganismeEtablissementQuery,
  useGetOrganismeEtablissementRncpsQuery,
  useReplaceOrganismeEtablissementRncpsMutation,
} from '@common/services';
import { RncpAjoutDto, RncpProjection } from '@common/services/rncp';
import { FcChip, FcPaginator, FcTable, getModalById } from '@common/ui';
import { CommonRncpFilters, RncpFilters } from '@common/ui/filters/rncp';
import { appToast } from '@common/ui/toast';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from "../../../../hooks/store.hook";
import { rncpModified } from "../../../Dashboard/components/VerticalStepper/stores/dashboard-stepper.slice";
import { LoadRncpModal } from './modals/LoadRncpModal';
import { RncpSelectionModal } from './modals/RncpSelectionModal';

const LOAD_RNCP_MODAL = getModalById('load-rncp-modal');
const SELECTED_RNCP_MODAL = getModalById('selected-rncp-modal');

const DEFAULT_FILTERS: RncpFilters = {
  search: '',
  typeCertification: null,
  niveauCertification: null,
};

const FILTERS_TEXTS: Record<keyof RncpFilters, string> = {
  search: 'Recherche',
  typeCertification: 'Type de certification',
  niveauCertification: 'Niveau de certification',
};

const DEFAULT_SORTERS: Sorter[] = [
  { field: 'rncp', direction: 'asc' },
];

export const EditEtablissementRncps = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const etablissementId = useNumberParam('etablissementId');
  const { organismeId } = useSessionContext();
  const { campagne } = useCurrentCampagne();

  const [page, setPage] = useState<number>(0);
  const [sorter, setSorter] = useState<Sorter | null>(null);
  const [filters, setFilters] = useState<RncpFilters>(DEFAULT_FILTERS);
  const [selectedRncpIds, setSelectedRncpIds] = useState<Set<number>>(new Set());

  const t = useTextes('ParcoursOfa', 'EtapierStep3');

  const {
    data: etablissement = {} as EtablissementInfoDto,
    isLoading: isLoadingEtablissement,
    error,
  } = useGetOrganismeEtablissementQuery({
    projection: EtablissementProjection.INFO,
    id: organismeId,
    etablissementId,
  });

  const { data: pageRncps = {} as Page<RncpAjoutDto> } = useGetCampagneRncpsQuery({
    id: campagne?.id,
    projection: RncpProjection.AJOUT,
    page,
    sorters: (sorter) ? [sorter] : DEFAULT_SORTERS,
    filters,
  }, {
    skip: !campagne,
    refetchOnMountOrArgChange: true,
  });

  // On réagit aux changements du Set des IDs sélectionnés dans la table pour mettre à jour la liste ordonnée des RNCPs
  const [selectedOrderedRncps, setSelectedOrderedRncps] = useSyncListByUniqueSelection({
    resources: pageRncps.content ?? [],
    selection: selectedRncpIds,
  });

  const { data: savedRncps } = useGetOrganismeEtablissementRncpsQuery({
    id: organismeId,
    etablissementId,
  });

  const [replaceOrganismeEtablissementRncps] = useReplaceOrganismeEtablissementRncpsMutation();
  const [downloadSelectedCampagneRncpsExcel, { isLoading: isExcelDownloading }] = useDownloadSelectedCampagneRncpsExcelMutation();

  const clickExportRNCPButton = () => {
    const rncpIds = selectedOrderedRncps.map(({ id }) => id);
    downloadSelectedCampagneRncpsExcel({ id: campagne?.id, rncpIds });
  };

  useEffect(() => {
    setPage(0);
  }, [filters]);

  // On met à jour les RNCPs sélectionnés, dans la table et dans la liste ordonnée, avec les données enregistrées depuis le serveur
  useEffect(() => {
    if (!savedRncps) return;

    setSelectedRncpIds(new Set(savedRncps.map(({ id }) => id)));
    setSelectedOrderedRncps(savedRncps);
  }, [savedRncps]);

  const handleLoadedRncpByExcel = (rncps: RncpAjoutDto[]) => {
    setSelectedRncpIds(new Set(rncps.map(({ id }) => id)));
    setSelectedOrderedRncps(rncps);
  };

  const goToEtablissementsList = () => navigate('/mon-ofa/edition/etablissements/rncp');

  const hasRncpSelectionChanged = (): boolean => {
    const savedIds = (savedRncps ?? []).map(({ id }) => id);
    const currentIds = selectedOrderedRncps.map(({ id }) => id);

    if (savedIds.length !== currentIds.length) return true;

    return savedIds.some((id, index) => id !== currentIds[index]);
  };

  const handleSave = async () => {
    try {
      const hasChanged = hasRncpSelectionChanged();

      await replaceOrganismeEtablissementRncps({
        id: organismeId,
        etablissementId,
        body: { rncps: selectedOrderedRncps.map(({ id: rncpId }, ordre) => ({ rncpId, ordre })) },
      }).unwrap();

      if (hasChanged) {
        dispatch(rncpModified());
      }

      goToEtablissementsList();
    } catch (err: unknown) {
      if (err.data.status === 404) {
        appToast.error('Un ou plusieurs RNCP n\'existe pas ou plus. Veuillez actualiser la page et réessayer.');
      }
    }
  };

  const handleRemove = (id: number) => {
    const selectedRncpsCopy = [...selectedOrderedRncps];

    const index = selectedRncpsCopy.findIndex((rncp) => rncp.id === id);
    if (index === -1) return;

    selectedRncpsCopy.splice(index, 1);

    setSelectedOrderedRncps(selectedRncpsCopy);
    setSelectedRncpIds(new Set(selectedRncpsCopy.map((rncp) => rncp.id)));

    if (selectedRncpsCopy.length === 0) SELECTED_RNCP_MODAL.close();
  };

  if (isLoadingEtablissement) return null;
  if (error?.status === 404) return <div>L'établissement n'existe pas ou n'appartient pas à cet organisme.</div>; // TODO Rediriger vers une page NotFound paramétrable

  return (
    <div className={'flex flex-col gap-4 items-center p-4 pb-8'}>

      <div className={'flex flex-col w-full gap-6 max-w-[1400px]'}>

        <div className={'flex flex-col w-full'}>

          <h3 className={'flex-1 !mb-1'}>{t['RNCP_OFA_TXT1']}</h3>

          <span className={'mt-4'}>{t['RNCP_OFA_TXT2']}</span>
        </div>

        <div className={'w-full flex flex-col gap-6 py-[56px] px-[102px] bg-white'}>

          <h6 className={'m-0!'}>{etablissement.denominationUsuelle}</h6>

          <div className={'flex justify-between gap-4'}>

            <Button priority="tertiary" onClick={() => LOAD_RNCP_MODAL.open()}>Charger mes RNCP</Button>

            <Button priority="tertiary" onClick={clickExportRNCPButton} disabled={isExcelDownloading}>Télécharger mes
              RNCP</Button>
          </div>

          <div className={'separator'} />

          <CommonRncpFilters filters={filters}
                             setFilters={setFilters}
                             defaultFilters={DEFAULT_FILTERS}
                             filtersTexts={FILTERS_TEXTS} />

          <div className={'separator'} />

          {(selectedRncpIds.size > 0) && (
            <div className={'flex flex-col gap-8 '}>

              <div className={'flex gap-6 items-center'}>
                <div className={'flex gap-2'}>
                  <div
                    className={'rounded border border-1 px-1 text-(--text-label-blue-france) border-(--text-label-blue-france)'}>
                    <span>{selectedOrderedRncps.length}</span>
                  </div>
                  <span
                    className={'text-(--text-label-blue-france)'}>RNCP</span>
                </div>

                <Button iconId="fr-icon-eye-line"
                        priority="primary"
                        onClick={() => SELECTED_RNCP_MODAL.open()}>VOIR</Button>
              </div>

              <div className={'flex gap-2 flex-wrap'}>
                {selectedOrderedRncps.map(({ id, rncp }, index) => (
                  <div key={index}>
                    <FcChip value={rncp} onRemove={() => handleRemove(id)} />
                  </div>
                ))}
              </div>

              <div className={'separator'} />
            </div>
          )}

          <FcTable<RncpAjoutDto>
            columnsConfig={[
              {
                header: { title: 'RNCP', sorting: true },
                field: 'rncp',
              },
              {
                header: { title: 'Code diplôme', sorting: true },
                field: 'codeDiplome',
              },
              {
                header: { title: 'Libellé', sorting: true },
                field: 'libelle',
              },
              {
                header: { title: 'Code spécialité', sorting: true },
                field: 'codeSpecialite',
              },
              {
                header: { title: 'Type de certification', sorting: true },
                field: 'typeCertification',
              },
              {
                header: { title: 'Niveau de la certification', sorting: true },
                field: 'niveauCertification',
              },
            ]}
            rowConfig={{
              selectionConfig: {
                fieldId: 'id',
                globalCheckbox: true,
                singleCheckbox: true,
                selection: selectedRncpIds,
                onSelectionChange: setSelectedRncpIds,
              },
            }}
            sorter={sorter}
            onSorterChange={setSorter}
            data={pageRncps?.content ?? []} />

          <div className={'flex flex-col items-center'}>
            <FcPaginator page={pageRncps.pageable?.pageNumber ?? 0}
                         totalPages={pageRncps?.totalPages ?? 0}
                         onPageChange={setPage} />
          </div>

          <div className={'flex justify-end gap-2'}>
            <Button style={{ color: '#E1000F', borderColor: '#E1000F', boxShadow: 'none' }}
                    priority={'secondary'}
                    onClick={() => goToEtablissementsList()}>ANNULER</Button>
            <Button onClick={() => handleSave()}>ENREGISTRER</Button>
          </div>
        </div>
      </div>

      <LoadRncpModal dsfrModal={LOAD_RNCP_MODAL}
                     onLoadedRncps={handleLoadedRncpByExcel} />

      <RncpSelectionModal dsfrModal={SELECTED_RNCP_MODAL}
                          selectedRncps={selectedOrderedRncps}
                          setSelectedRncps={setSelectedOrderedRncps}
                          onDelete={handleRemove} />
    </div>
  );
};
